import { useMemo, useState, type ComponentType, type ReactNode } from 'react'
import {
  ArrowSquareOut,
  BuildingOffice,
  Check,
  CustomerSupport,
  Headset,
  Info,
  File,
  Satellite,
  Sparkles,
  TrendUp,
  Workflow,
  type IconProps,
} from './ui/icons'
import {
  calculateEnrollmentRoi,
  DEFAULT_ENROLLMENT_INPUT,
  INSTITUTION_DEFAULTS,
  type EnrollmentRoiInput,
  type InstitutionType,
  type TermYears,
} from '../calculators/enrollment'
import {
  calculateLicensePricing,
  DEFAULT_LICENSE_INPUT,
  INCLUDED_LICENSE_FEATURES,
  LICENSE_MODULES,
  type IncludedLicenseFeature,
  type LicenseModuleId,
  type LicensePricingInput,
  type SupportTier,
} from '../calculators/license'
import { compactMoney, money, number } from '../lib/format'
import { houseNoun } from '../lib/house-noun'
import type { CustomerSegment, PricingModel, WorkspaceQuote } from '../types'

interface PricingCalculatorProps {
  model: PricingModel
  segment?: CustomerSegment | null
  initialInputs?: Record<string, unknown>
  onSave?: (inputs: Record<string, unknown>, output: Record<string, unknown>) => void
  onGenerateQuote?: (inputs: Record<string, unknown>) => Promise<WorkspaceQuote>
  quotes?: WorkspaceQuote[]
}

const LICENSE_MODULE_ICONS: Record<LicenseModuleId, ComponentType<IconProps>> = {
  matching: Satellite,
  employer: BuildingOffice,
  outcomes: TrendUp,
  agent: Sparkles,
}

function TermPicker({ value, onChange }: { value: TermYears; onChange: (term: TermYears) => void }) {
  const options: Array<{ term: TermYears; detail: string }> = [
    { term: 1, detail: 'The full one-year contract is paid at signature and remains eligible for the 90-day opt-out.' },
    { term: 2, detail: 'Implementation is 50% off. The full two-year contract is paid at signature.' },
    { term: 3, detail: 'Implementation is waived. The full three-year contract is paid at signature.' },
  ]
  const selectedOption = options.find((option) => option.term === value) || options[0]
  return (
    <fieldset className="term-picker">
      <legend>Agreement term</legend>
      <div className="term-options">
        {options.map((option) => (
          <button
            type="button"
            key={option.term}
            className={value === option.term ? 'term-option active' : 'term-option'}
            aria-pressed={value === option.term}
            onClick={() => onChange(option.term)}
          >
            <strong>{option.term} year{option.term > 1 ? 's' : ''}</strong>
          </button>
        ))}
      </div>
      <p className="term-detail"><Check size={16} weight="bold" aria-hidden="true" />{selectedOption.detail}</p>
    </fieldset>
  )
}

function PaymentSummary({
  term,
  onTermChange,
  annualPlatform,
  total,
  paymentSchedule,
  quoteActions,
}: {
  term: TermYears
  onTermChange: (term: TermYears) => void
  annualPlatform: number
  total: number
  paymentSchedule: Array<{ label: string; timing: string; amount: number; originalAmount?: number; retained?: boolean; included?: boolean }>
  quoteActions?: ReactNode
}) {
  const implementationLine = paymentSchedule.find((line) => line.included)
  const scheduleLines = paymentSchedule.filter((line) => !line.included)
  const contractDetail = term === 3
    ? 'Annual fee × 3 · implementation waived'
    : term === 2
      ? 'Annual fee × 2 + discounted implementation'
      : 'Annual fee + implementation'

  return (
    <aside className="quote-column">
      <TermPicker value={term} onChange={onTermChange} />
      <section className="quote-summary" aria-label="Partnership investment">
        <div className="quote-kicker">Partnership investment</div>
        <div className="quote-annual">
          <span>Annual partnership fee</span>
          <strong>{money(annualPlatform)}</strong>
          <small>per year</small>
        </div>
        {implementationLine && (
          <div className="quote-implementation">
            <div>
              <span>{implementationLine.label}</span>
              <small>{implementationLine.timing}</small>
            </div>
            <strong className={implementationLine.originalAmount !== undefined ? 'discounted' : ''}>
              {implementationLine.originalAmount !== undefined && <s>{money(implementationLine.originalAmount)}</s>}
              <span>{implementationLine.amount === 0 ? 'Waived' : money(implementationLine.amount)}</span>
            </strong>
          </div>
        )}
        <div className="quote-contract-total">
          <span>{term}-year contract total</span>
          <strong>{money(total)}</strong>
          <small>{contractDetail}</small>
        </div>
        <div className="payment-schedule">
          <h3>Payment schedule</h3>
          {scheduleLines.map((line) => (
            <div className={line.retained ? 'schedule-line opt-out' : 'schedule-line'} key={`${line.label}-${line.timing}`}>
              <div>
                <strong>{line.label}</strong>
                <span>{line.timing}</span>
              </div>
              <b className={line.originalAmount !== undefined ? 'schedule-amount discounted' : 'schedule-amount'}>
                {line.originalAmount !== undefined && <s>{money(line.originalAmount)}</s>}
                <span>{line.retained ? `${money(line.amount)} non-refundable` : money(line.amount)}</span>
              </b>
            </div>
          ))}
        </div>
        <p className="opt-out-note">
          <Info size={17} weight="bold" aria-hidden="true" />
          The full contract is paid at signature. During the 90-day opt-out window, Prentus refunds the prepaid partnership fee and retains only the implementation fee shown above.
        </p>
      </section>
      {quoteActions}
    </aside>
  )
}

export function PricingCalculator({ model, segment, initialInputs = {}, onSave, onGenerateQuote, quotes = [] }: PricingCalculatorProps) {
  return model === 'license'
    ? <LicenseCalculator segment={segment} initialInputs={initialInputs} onSave={onSave} onGenerateQuote={onGenerateQuote} quotes={quotes} />
    : <EnrollmentCalculator initialInputs={initialInputs} onSave={onSave} onGenerateQuote={onGenerateQuote} quotes={quotes} />
}

function EnrollmentCalculator({ initialInputs, onSave, onGenerateQuote, quotes }: Omit<PricingCalculatorProps, 'model'>) {
  const [input, setInput] = useState<EnrollmentRoiInput>({
    ...DEFAULT_ENROLLMENT_INPUT,
    ...initialInputs,
  } as EnrollmentRoiInput)
  const result = useMemo(() => calculateEnrollmentRoi(input), [input])

  function setType(type: InstitutionType) {
    const defaults = INSTITUTION_DEFAULTS[type]
    setInput((current) => ({
      ...current,
      institutionType: type,
      enrolled: defaults.enrolled,
      revenuePerStudent: defaults.revenuePerStudent,
      attritionPercent: defaults.attritionPercent,
      advisors: defaults.advisors,
    }))
  }

  const ratio = Math.max(1, result.totalYearOneValue / Math.max(1, result.totalContract))
  const costWidth = Math.max(2.5, Math.min(28, 100 / ratio))

  return (
    <div className="calculator-layout">
      <section className="calculator-main">
        <div className="calculator-heading">
          <div>
            <h2>Model the value for your institution</h2>
            <p>Adjust the assumptions to match your enrollment, staffing, and rollout. Total enrollment sets the subscription band.</p>
          </div>
          {onSave && <button type="button" className="button button-secondary" onClick={() => onSave(input as unknown as Record<string, unknown>, result as unknown as Record<string, unknown>)}>Save pricing</button>}
        </div>

        <div className="institution-picker" role="group" aria-label="Institution type">
          {(Object.entries(INSTITUTION_DEFAULTS) as Array<[InstitutionType, (typeof INSTITUTION_DEFAULTS)[InstitutionType]]>).map(([key, item]) => (
            <button type="button" key={key} className={input.institutionType === key ? 'active' : ''} onClick={() => setType(key)}>{item.label}</button>
          ))}
        </div>

        <div className="field-grid">
          <NumberField label="Total enrollment" value={input.enrolled} onChange={(enrolled) => setInput((current) => ({ ...current, enrolled }))} />
          <NumberField label="Revenue per student" value={input.revenuePerStudent} prefix="$" onChange={(revenuePerStudent) => setInput((current) => ({ ...current, revenuePerStudent }))} />
          <NumberField label="First-year attrition" value={input.attritionPercent} suffix="%" onChange={(attritionPercent) => setInput((current) => ({ ...current, attritionPercent }))} />
          <NumberField label="Current career advisors" value={input.advisors} onChange={(advisors) => setInput((current) => ({ ...current, advisors }))} />
        </div>

        <label className="range-field">
          <span><strong>Expected retention lift</strong><b>{input.retentionLiftPercent} points</b></span>
          <input type="range" min="0.5" max="5" step="0.5" value={input.retentionLiftPercent} onChange={(event) => setInput((current) => ({ ...current, retentionLiftPercent: Number(event.target.value) }))} />
        </label>

        <section className="roi-result">
          <div className="roi-number">
            <span>Estimated year-one value</span>
            <strong>{compactMoney(result.totalYearOneValue)}+</strong>
            <small>{result.roiMultiple.toFixed(1)}x the total contract investment</small>
          </div>
          <div className="roi-breakdown">
            <Metric label="Students retained" value={number(result.studentsRetained)} />
            <Metric label="Tuition kept" value={money(result.tuitionKept)} />
            <Metric label="Recruitment avoided" value={`${money(result.recruitmentAvoided)}+`} />
            <Metric label="Staff capacity value" value={money(result.staffingValue)} />
          </div>
          <div className="roi-breakdown shared-value-components" aria-label="Annual value components">
            {result.sharedValue.components.map((component) => <Metric key={component.id} label={component.label} value={money(component.amount)} />)}
          </div>
          <details className="roi-assumptions">
            <summary>Value model assumptions</summary>
            <ul>{result.sharedValue.assumptions.map((assumption) => <li key={assumption}>{assumption}</li>)}</ul>
          </details>
        </section>

        <section className="roi-comparison" aria-label="ROI comparison">
          <div className="comparison-label"><span>Estimated value</span><strong>{compactMoney(result.totalYearOneValue)}</strong></div>
          <div className="value-bar"><span style={{ width: '100%' }} /></div>
          <div className="comparison-label cost"><span>Prentus investment</span><strong>{compactMoney(result.totalContract)}</strong></div>
          <div className="value-bar cost"><span style={{ width: `${costWidth}%` }} /></div>
        </section>

        <div className="pricing-basis">
          <div><span>Enrollment pricing band</span><strong>{result.pricingBand} students</strong></div>
          <b>{money(result.annualPlatform)}<small> / year</small></b>
        </div>
        <ImplementationScopePicker value={input.implementationScope} onChange={(implementationScope) => setInput((current) => ({ ...current, implementationScope }))} />
      </section>
      <PaymentSummary
        term={input.termYears}
        onTermChange={(termYears) => setInput((current) => ({ ...current, termYears }))}
        annualPlatform={result.annualPlatform}
        total={result.totalContract}
        paymentSchedule={result.paymentSchedule}
        quoteActions={onGenerateQuote ? <QuoteActions inputs={input as unknown as Record<string, unknown>} onGenerate={onGenerateQuote} quotes={quotes || []} /> : undefined}
      />
    </div>
  )
}

function LicenseCalculator({ segment, initialInputs, onSave, onGenerateQuote, quotes }: Omit<PricingCalculatorProps, 'model'>) {
  const [input, setInput] = useState<LicensePricingInput>({ ...DEFAULT_LICENSE_INPUT, ...initialInputs } as LicensePricingInput)
  const result = useMemo(() => calculateLicensePricing(input, segment), [input, segment])
  const noun = houseNoun(segment)

  function toggleModule(module: LicenseModuleId) {
    setInput((current) => ({
      ...current,
      selectedModules: current.selectedModules.includes(module)
        ? current.selectedModules.filter((item) => item !== module)
        : [...current.selectedModules, module],
    }))
  }

  const validSupport: SupportTier = result.billedLicenses > 20_000 ? 'enterprise' : 'professional'
  const upgradeSupportAnnual = calculateLicensePricing({ ...input, supportTier: validSupport }, segment).supportAnnual
  const modulesNeededForDiscount = Math.max(0, 3 - input.selectedModules.length)
  const valueRatio = Math.max(1, result.annualEstimatedValue / Math.max(1, result.annualSubscription))
  const costWidth = Math.max(2.5, Math.min(100, 100 / valueRatio))

  return (
    <div className="calculator-layout">
      <section className="calculator-main">
        <div className="calculator-heading">
          <div><h2>Build your Prentus plan</h2><p>Set active {noun.plural}, choose a term, and add the capabilities your team needs.</p></div>
          {onSave && <button type="button" className="button button-secondary" onClick={() => onSave(input as unknown as Record<string, unknown>, result as unknown as Record<string, unknown>)}>Save pricing</button>}
        </div>

        <div className="license-intro">
          <NumberField label={`Expected ${noun.plural} who will activate each year`} value={input.annualLicenses} onChange={(annualLicenses) => setInput((current) => ({ ...current, annualLicenses }))} />
          <div className="rate-callout"><strong>${result.perLicenseRate} <span>per license / year</span></strong><small>{number(result.pooledLicenses)} pooled licenses</small>{result.perLicenseSavings > 0 && <em>Save ${result.perLicenseSavings} per license / year</em>}</div>
        </div>
        {input.annualLicenses > 0 && input.annualLicenses < 500 && <p className="inline-notice">Prentus has a 500-license annual minimum. This estimate uses 500 paid licenses.</p>}

        <aside className="license-explainer" aria-labelledby="license-explainer-title">
          <Info size={20} aria-hidden="true" />
          <div>
            <h3 id="license-explainer-title">How licenses work</h3>
            <p><strong>Invite freely.</strong> A license starts only when a {noun.singular} completes onboarding and begins a search; it stays with that person until they are hired.</p>
            <p><strong>Use licenses across the term.</strong> Multi-year licenses are pooled, so 500 per year gives your team 1,000 activations to use anytime over two years.</p>
          </div>
        </aside>

        <section className="included-list">
          <h3>Included in every license</h3>
          <div>{INCLUDED_LICENSE_FEATURES.map((feature) => <IncludedFeatureChip key={feature.name} feature={feature} />)}</div>
        </section>

        <section className="module-section">
          <div className="section-row"><div><h3>Choose product modules</h3><p>Build the experience around the capabilities your team needs.</p></div><span>{input.selectedModules.length}/4 selected</span></div>
          {modulesNeededForDiscount > 0
            ? <div className="bundle-status"><span>Add <strong>{modulesNeededForDiscount} more module{modulesNeededForDiscount === 1 ? '' : 's'}</strong> to unlock the <strong>20% bundle discount</strong>.</span></div>
            : <div className="bundle-status active"><Check size={17} weight="bold" /><span><strong>20% bundle discount active</strong> across selected modules</span><b>-{money(result.bundleDiscountAnnual)} / year</b></div>}
          <div className="module-grid">
            {LICENSE_MODULES.map((module) => {
              const selected = input.selectedModules.includes(module.id)
              return (
                <LicenseOptionCard
                  key={module.id}
                  selected={selected}
                  icon={LICENSE_MODULE_ICONS[module.id]}
                  name={module.name}
                  description={module.description}
                  price={`$${module.price}`}
                  suffix="/ license / year"
                  detail="Billed annually"
                  onClick={() => toggleModule(module.id)}
                />
              )
            })}
          </div>
        </section>

        <section className="addon-section">
          <h3>Add-ons and support</h3>
          <div className="service-group">
            <span className="service-label">Optional add-on</span>
            <LicenseOptionCard
              selected={input.automatedWorkflows}
              icon={Workflow}
              name="Automated Workflows"
              description="Bidirectional data sync through APIs and webhooks, with custom triggers for your connected systems."
              price="$3"
              suffix="/ license / year"
              detail="Billed annually"
              wide
              onClick={() => setInput((current) => ({ ...current, automatedWorkflows: !current.automatedWorkflows }))}
            />
          </div>
          <div className="service-divider" />
          <div className="service-group">
            <span className="service-label">Support tier</span>
            <div className="support-grid" role="group" aria-label="Support tier">
              <LicenseOptionCard
                selected={input.supportTier === 'standard'}
                icon={CustomerSupport}
                name="Standard"
                description="Unlimited chat, 24-hour response, and five onboarding hours."
                price="Included"
                onClick={() => setInput((current) => ({ ...current, supportTier: 'standard' }))}
              />
              <LicenseOptionCard
                selected={input.supportTier === validSupport}
                icon={Headset}
                name={validSupport === 'enterprise' ? 'Enterprise 24/7' : 'Professional'}
                description={validSupport === 'enterprise' ? 'Eight-hour response, dedicated CSM, weekly syncs, SLA, and 20 hours per quarter.' : 'Eight-hour response, monthly CSM, quarterly review, and 10 hours per quarter.'}
                price={validSupport === 'professional' ? '$2' : money(upgradeSupportAnnual)}
                suffix={validSupport === 'professional' ? '/ license / year' : '/ year'}
                onClick={() => setInput((current) => ({ ...current, supportTier: validSupport }))}
              />
            </div>
          </div>
        </section>

        <section className="license-roi-section">
          <div className="section-row"><div><h3>Estimate the annual return</h3><p>Use your own operating assumptions. Nothing below changes the quote.</p></div></div>
          <div className="field-grid license-roi-inputs">
            <NumberField label="Revenue per student" value={input.revenuePerStudent ?? 0} prefix="$" onChange={(revenuePerStudent) => setInput((current) => ({ ...current, revenuePerStudent }))} />
            <NumberField label="Current career advisors" value={input.advisors} onChange={(advisors) => setInput((current) => ({ ...current, advisors }))} />
            <NumberField label="Additional job outcomes each year" value={input.additionalOutcomes ?? 0} onChange={(additionalOutcomes) => setInput((current) => ({ ...current, additionalOutcomes }))} />
            <NumberField label="Estimated value per job outcome" value={input.valuePerOutcome} prefix="$" onChange={(valuePerOutcome) => setInput((current) => ({ ...current, valuePerOutcome }))} />
          </div>
          <label className="range-field">
            <span><strong>Expected retention lift</strong><b>{input.retentionLiftPercent} points</b></span>
            <input type="range" min="0.5" max="5" step="0.5" value={input.retentionLiftPercent} onChange={(event) => setInput((current) => ({ ...current, retentionLiftPercent: Number(event.target.value) }))} />
          </label>
          <section className="roi-result compact">
            <div className="roi-number"><span>Estimated annual value</span><strong>{compactMoney(result.annualEstimatedValue)}</strong><small>{result.roiMultiple.toFixed(1)}x the annual subscription</small></div>
            <div className="roi-breakdown shared-value-components" aria-label="Annual value components">
              {result.sharedValue.components.map((component) => <Metric key={component.id} label={component.label} value={money(component.amount)} />)}
            </div>
            <details className="roi-assumptions">
              <summary>Value model assumptions</summary>
              <ul>{result.sharedValue.assumptions.map((assumption) => <li key={assumption}>{assumption}</li>)}</ul>
            </details>
          </section>
          <section className="roi-comparison" aria-label="License ROI comparison">
            <div className="comparison-label"><span>Estimated annual value</span><strong>{compactMoney(result.annualEstimatedValue)}</strong></div>
            <div className="value-bar"><span style={{ width: '100%' }} /></div>
            <div className="comparison-label cost"><span>Annual Prentus subscription</span><strong>{compactMoney(result.annualSubscription)}</strong></div>
            <div className="value-bar cost"><span style={{ width: `${costWidth}%` }} /></div>
          </section>
        </section>

        <ImplementationScopePicker value={input.implementationScope} onChange={(implementationScope) => setInput((current) => ({ ...current, implementationScope }))} />

      </section>
      <PaymentSummary
        term={input.termYears}
        onTermChange={(termYears) => setInput((current) => ({ ...current, termYears }))}
        annualPlatform={result.annualSubscription}
        total={result.totalContract}
        paymentSchedule={result.paymentSchedule}
        quoteActions={onGenerateQuote ? <QuoteActions inputs={input as unknown as Record<string, unknown>} onGenerate={onGenerateQuote} quotes={quotes || []} /> : undefined}
      />
    </div>
  )
}

/** Exported so PricingCalculatorV2 (../calculators/pricing-v2) can reuse the same "Generate quote" card and quote history list rather than duplicating this UI. */
export function QuoteActions({ inputs, onGenerate, quotes }: { inputs: Record<string, unknown>; onGenerate: (inputs: Record<string, unknown>) => Promise<WorkspaceQuote>; quotes: WorkspaceQuote[] }) {
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')
  const [latest, setLatest] = useState<WorkspaceQuote | null>(null)
  const visibleQuotes = latest ? [latest, ...quotes.filter((quote) => quote.id !== latest.id)] : quotes

  async function generate() {
    if (creating) return
    setCreating(true)
    setError('')
    try {
      setLatest(await onGenerate(inputs))
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The quote could not be generated.')
    } finally {
      setCreating(false)
    }
  }

  return (
    <section className="quote-generator-card" aria-labelledby="quote-generator-title">
      <div className="quote-generator-copy">
        <span className="quote-generator-icon"><File size={21} aria-hidden="true" /></span>
        <div><h3 id="quote-generator-title">Ready to share with your team?</h3><p>Generate a board-ready pricing proposal using the options selected above. The PDF is valid for 30 days and the final order form controls.</p></div>
      </div>
      <button type="button" className="button button-primary quote-generate-button" disabled={creating} onClick={() => void generate()}>{creating ? 'Generating quote…' : 'Generate quote'}</button>
      {error && <p className="form-error" role="alert">{error}</p>}
      {visibleQuotes.length > 0 && <QuoteHistory quotes={visibleQuotes} label={latest ? 'Quote ready' : 'Recent quotes'} />}
    </section>
  )
}

/** The three newest quotes as PDF links. The customer pricing tab shows the same list once the calculator is locked. */
export function QuoteHistory({ quotes, label }: { quotes: WorkspaceQuote[]; label: string }) {
  return (
    <div className="quote-history">
      <span>{label}</span>
      {quotes.slice(0, 3).map((quote) => (
        <a key={quote.id} href={quote.downloadUrl} target="_blank" rel="noreferrer">
          <span><strong>{quote.quoteNumber}</strong><small>Valid through {new Date(`${quote.validUntil}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}</small></span>
          <span>Open PDF<ArrowSquareOut size={15} /></span>
        </a>
      ))}
    </div>
  )
}

function IncludedFeatureChip({ feature }: { feature: IncludedLicenseFeature }) {
  const tooltipId = `included-feature-tooltip-${feature.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
  return (
    <span className="included-feature">
      <button type="button" className="included-feature-chip" aria-describedby={tooltipId} title={feature.details.join(', ')}>
        <Check size={16} weight="bold" aria-hidden="true" />
        <span className="included-feature-name">{feature.name}</span>
        <Info size={13} aria-hidden="true" />
      </button>
      <span className="included-feature-tooltip" role="tooltip" id={tooltipId}>
        <ul>{feature.details.map((detail) => <li key={detail}>{detail}</li>)}</ul>
      </span>
    </span>
  )
}

function LicenseOptionCard({
  selected,
  icon: Icon,
  name,
  description,
  price,
  suffix,
  detail,
  wide = false,
  onClick,
}: {
  selected: boolean
  icon: ComponentType<IconProps>
  name: string
  description: string
  price: string
  suffix?: string
  detail?: string
  wide?: boolean
  onClick: () => void
}) {
  return (
    <button type="button" className={`license-option-card${selected ? ' selected' : ''}${wide ? ' wide' : ''}`} aria-pressed={selected} onClick={onClick}>
      <span className="license-option-head">
        <span className="license-option-icon"><Icon size={21} aria-hidden="true" /></span>
        <span className="license-option-copy"><strong>{name}</strong><span>{description}</span></span>
        <span className="license-option-check">{selected && <Check size={16} weight="bold" />}</span>
      </span>
      <span className="license-option-foot">
        <span className="license-option-price"><b>{price}</b>{suffix && <small>{suffix}</small>}</span>
        {detail && <small>{detail}</small>}
      </span>
    </button>
  )
}

function ImplementationScopePicker({ value, onChange }: { value: 1 | 1.5 | 2; onChange: (scope: 1 | 1.5 | 2) => void }) {
  const options: Array<{ value: 1 | 1.5 | 2; title: string; description: string }> = [
    { value: 1, title: 'Standard', description: 'Platform configuration, program setup, admin training, and guided launch support.' },
    { value: 1.5, title: 'Deep integration', description: 'Adds SSO, data migration, workflow mapping, and tailored AI configuration.' },
    { value: 2, title: 'Complex integration', description: 'Adds custom SIS, LMS, or API work, multi-unit rollout, and deeper change management.' },
  ]
  return (
    <fieldset className="implementation-picker">
      <legend>Implementation scope</legend>
      <p>Choose the level that best reflects the systems and teams involved in launch.</p>
      <div>
        {options.map((option) => (
          <button type="button" key={option.value} className={value === option.value ? 'active' : ''} aria-pressed={value === option.value} onClick={() => onChange(option.value)}>
            <span className="scope-check">{value === option.value && <Check size={13} weight="bold" />}</span>
            <span><strong>{option.title}</strong><small>{option.description}</small></span>
          </button>
        ))}
      </div>
      <div className="implementation-delivery" aria-label="How Prentus delivers implementation">
        <h3>What the implementation fee covers</h3>
        <ol>
          <li><b>1</b><span><strong>Diagnose</strong><small>Understand your workflows, goals, constraints, and measures of success.</small></span></li>
          <li><b>2</b><span><strong>Configure</strong><small>Set up the platform, integrations, interventions, and AI around the way your team works.</small></span></li>
          <li><b>3</b><span><strong>Launch</strong><small>Train the team, support adoption, and make the first workflows useful from day one.</small></span></li>
          <li><b>4</b><span><strong>Prove</strong><small>Monitor activation, prove outcomes, and adjust with you through the first 90 days.</small></span></li>
        </ol>
      </div>
    </fieldset>
  )
}

// Exported so other admin surfaces (the qualification card's estimated
// value field) can reuse the same prefixed/suffixed number field markup
// instead of a divergent native input.
export function NumberField({ label, value, onChange, prefix, suffix }: { label: string; value: number; onChange: (value: number) => void; prefix?: string; suffix?: string }) {
  return (
    <label className="number-field">
      <span>{label}</span>
      <div>{prefix && <b>{prefix}</b>}<input type="number" min="0" value={value} onChange={(event) => onChange(Math.max(0, Number(event.target.value)))} />{suffix && <b>{suffix}</b>}</div>
    </label>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return <div className="metric"><TrendUp size={17} aria-hidden="true" /><span>{label}</span><strong>{value}</strong></div>
}
